import React, { Children, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { isEmpty } from "lodash";
// enums
import EContentStatus from "@/cms/enums/EContentStatus";
// hooks
import usePagePayload from "@/hooks/usePagePayload";
import useContentList from "@/hooks/useContentSearch";


type TContentSearch = {
    unqKey:string;
    filters?:string[];
    itemsPerPage?:string;
    sort?:string;
    children:any;
}

const insertBetween = ( payload:any[], value:any ) => {
    if ( isEmpty(payload) ) return [];
    return payload.flatMap((x) => [value, x]).slice(1);
}

const searchParamsToOdataQuery = (searchParams:URLSearchParams, init:any) => {
    // filters
    let filters: string[] = [...init.filters];
    let q:string|undefined = init?.q,
        queryString:string|undefined,
        tagIdQuery:string|undefined;

    // orders
    let orders: string[] = [...init.orders];
    let sort:string = init.sort;
    // pagination
        // top
    let top:string = init.itemsPerPage;
        // skip
    let currentPage:number = parseInt(init.currentPage);
    
    for ( let [key, value] of searchParams.entries() ) {
        if ( key === "q" ) {
            queryString = `(languageVersions/any(d: contains(tolower(d/value),'${value}')) or languageVersions/any(d: contains(tolower(d/name),'${value}')))`;
            q = value;
        }
        else if ( key === "top" )  // items per page
            top = value;
        else if ( key === "page" )     // current page number
            currentPage = parseInt(value);
        else if ( key === "sort" )    // order by
            sort = value;
        else if ( key === "tids" && !isEmpty(value))
            tagIdQuery= `tags/any(d: d/id in (${value}))`;
    }

    let skip:number = parseInt(top)*(currentPage-1);
    
    if (queryString) filters.push(queryString);
    if (tagIdQuery) filters.push(tagIdQuery);
    if (sort) orders.push(sort);
    
    filters = insertBetween(filters, "and");

    return [
        { key: "filters", value: filters },
        { key: "orders", value: orders },
        { key: "top", value: top },
        { key: "skip", value: skip },
        { key: "currentPage", value: currentPage },
        { key: "sort", value: sort },
        { key: "q", value: q }
    ]
}

const ContentSearch = (props:TContentSearch) => {
    
    const [ searchParams, setSearchParams ] = useSearchParams();
    const { unqKey, filters = [], children } = props;

    const pagePayload = usePagePayload(`${unqKey}PP`);
    const contentPayload = useContentList(`${unqKey}CP`);

    const [ searchParamsInit ] = useState({
        filters:[`status eq ${EContentStatus.ACTIVE}`, "hasDestination eq true", ...filters ],
        // filters:[`status eq ${EContentStatus.ACTIVE}`, "hasDestination eq true"],
        orders: [],
        itemsPerPage: props.itemsPerPage||"12",
        currentPage: 1,
        sort: props.sort||"creationDateTime desc"
    });

    const [ pageInit ] = useState([
        { key: "filters", value: null },
        { key: "orders", value: null },
        { key: "top", value: searchParamsInit.itemsPerPage },
        { key: "skip", value: 0 },

        { key: "lastPage", value: 0 },
        { key: "currentPage", value: searchParamsInit.currentPage },

        { key: "scrollTo", value: false },
        { key: "hasSearch", value: false }
    ]);

    useEffect(
        () => {
            pagePayload?.modifySearchPageStore(pageInit);
        }, []
    )

    useEffect (
        () => {
            const payload = searchParamsToOdataQuery(searchParams, searchParamsInit);
            pagePayload?.modifySearchPageStore([...payload, { key:"hasSearch", value: true }, { key:"scrollTo", value: true }])
        }, [ searchParams ]
    )

    useEffect(
        () => {
            if ( contentPayload?.count && pagePayload?.payload.top ) {
                try {
                    let _lastPage = Math.ceil(contentPayload?.count/pagePayload?.payload.top);
                    pagePayload?.setLastPage(_lastPage);
                }
                catch(ex) {}
            }
        }, [contentPayload?.count, pagePayload?.payload?.top]
    )

    useEffect(
        () => {
            if (pagePayload?.payload?.hasSearch) {
                pagePayload?.setHasSearchFalse();
                contentPayload?.search(pagePayload?.payload);
                window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
            }
        }, [pagePayload?.payload]
    )

    return Children.map(children, (child:any) => React.cloneElement(child, { params: { ...child?.props?.params, searchPagePayload: pagePayload?.payload, payloadList: contentPayload?.payload, payloadListIsLoading: contentPayload?.isLoading, payloadListCount: contentPayload?.count } }))
}

export default ContentSearch;
